import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { NODE_UI, stopPropagation } from './nodeUiStyles';
import NodeAddButton from './NodeAddButton';

/**
 * NodeRepeatableList — Generic repeatable card list with add/remove/reorder.
 * Used for output placeholders, key-value rows, conditions, etc.
 *
 * The caller provides a `renderItem` function that receives the item, index,
 * and update/remove callbacks. This keeps the list generic while allowing
 * custom field layouts per use case.
 *
 * Usage:
 *   <NodeRepeatableList
 *     label="Output Placeholders"
 *     items={outputPlaceholders}
 *     onItemsChange={(newItems) => setOutputPlaceholders(newItems)}
 *     createItem={() => ({ key: '', type: 'text', description: '' })}
 *     renderItem={(item, index, { updateItem, removeItem }) => (
 *       <div>
 *         <NodePlaceholderTextField
 *           value={item.key}
 *           onChange={(val) => updateItem(index, { ...item, key: val })}
 *         />
 *         <IconButton onClick={() => removeItem(index)}>
 *           <DeleteOutlineIcon />
 *         </IconButton>
 *       </div>
 *     )}
 *     addLabel="Add Output"
 *   />
 */

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
  width: '100%',
});

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  userSelect: 'none',
}));

const ItemCard = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: NODE_UI.spacing.sm,
  padding: NODE_UI.spacing.md,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: theme.palette.background.paper,
  position: 'relative',
  '&:hover': {
    borderColor: theme.palette.text.secondary,
  },
  '&:hover .drag-handle': {
    opacity: 1,
  },
}));

const DragHandle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'grab',
  opacity: 0,
  transition: 'opacity 0.15s',
  color: theme.palette.text.secondary,
  padding: '2px 0',
  flexShrink: 0,
  '&:active': {
    cursor: 'grabbing',
  },
}));

const ItemContent = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
});

const DeleteButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: 'transparent',
  },
}));

const EmptyState = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  padding: `${NODE_UI.spacing.lg} 0`,
}));

const NodeRepeatableList = React.memo(function NodeRepeatableList({
  label,
  items = [],
  onItemsChange,
  renderItem,
  createItem,
  addLabel = 'Add',
  emptyMessage,
  showDragHandle = false,
  showDeleteButton = true,
  maxItems,
  minItems = 0,
  disabled = false,
  sx,
}) {
  const canAdd = !disabled && (!maxItems || items.length < maxItems);
  const canRemove = !disabled && items.length > minItems;

  const updateItem = useCallback((index, newItem) => {
    const next = [...items];
    next[index] = newItem;
    onItemsChange?.(next);
  }, [items, onItemsChange]);

  const removeItem = useCallback((index) => {
    if (!canRemove) return;
    const next = items.filter((_, i) => i !== index);
    onItemsChange?.(next);
  }, [items, canRemove, onItemsChange]);

  const addItem = useCallback(() => {
    if (!canAdd || !createItem) return;
    const newItem = createItem(items.length);
    onItemsChange?.([...items, newItem]);
  }, [items, canAdd, createItem, onItemsChange]);

  const moveItem = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onItemsChange?.(next);
  }, [items, onItemsChange]);

  return (
    <Root
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      sx={sx}
    >
      {label && <Label>{label}</Label>}

      {items.length === 0 && emptyMessage && (
        <EmptyState>{emptyMessage}</EmptyState>
      )}

      {items.map((item, index) => (
        <ItemCard key={item._id || item.id || index}>
          {showDragHandle && (
            <DragHandle className="drag-handle">
              <DragIndicatorIcon sx={{ fontSize: 16 }} />
            </DragHandle>
          )}

          <ItemContent>
            {renderItem(item, index, { updateItem, removeItem, moveItem })}
          </ItemContent>

          {showDeleteButton && canRemove && (
            <DeleteButton
              size="small"
              onClick={(e) => {
                stopPropagation(e);
                removeItem(index);
              }}
              onMouseDown={stopPropagation}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </DeleteButton>
          )}
        </ItemCard>
      ))}

      {canAdd && createItem && (
        <NodeAddButton
          label={addLabel}
          onClick={addItem}
          disabled={disabled}
        />
      )}
    </Root>
  );
});

export default NodeRepeatableList;
